import axios from 'axios';
import { Campaign, CampaignWithDetails, CampaignFilters } from '../types';

// Use environment variable or default to dev API
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'https://1kdr5qige8.execute-api.us-east-1.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add basic auth for admin endpoints (for internal use)
api.interceptors.request.use((config) => {
  // In a real implementation, this would be more secure
  const adminAuth = btoa('admin:tastuk-admin-2024');
  config.headers.Authorization = `Basic ${adminAuth}`;
  return config;
});

export const campaignApi = {
  // Get all campaigns with admin filtering
  getCampaigns: async (filters?: CampaignFilters): Promise<Campaign[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.search) params.append('search', filters.search);
    
    const response = await api.get(`/admin/campaigns?${params.toString()}`);
    return response.data.campaigns;
  },

  // Get campaign details with recipients and order info
  getCampaignDetails: async (campaignId: string): Promise<CampaignWithDetails> => {
    const response = await api.get(`/campaigns/${campaignId}`);
    const campaign = response.data.campaign;

    // Get recipients
    const recipientsResponse = await api.get(`/campaigns/${campaignId}/recipients`);
    const recipients = recipientsResponse.data.recipients;

    // For now, we don't have a separate order endpoint
    // Order information would be part of the campaign status
    const order = null;

    return {
      ...campaign,
      recipients,
      order,
    };
  },

  // Update campaign status (admin only)
  updateCampaignStatus: async (campaignId: string, status: string): Promise<void> => {
    await api.put(`/campaigns/${campaignId}/status`, { status });
  },

  // Get export file download URL
  getExportUrl: async (campaignId: string, fileType: 'csv' | 'json'): Promise<string> => {
    const response = await api.get(`/campaigns/${campaignId}/export?type=${fileType}`);
    return response.data.downloadUrl;
  },

  // Get asset download URLs
  getAssetUrls: async (campaignId: string): Promise<string[]> => {
    const response = await api.get(`/campaigns/${campaignId}/assets`);
    return response.data.assets;
  },
};

export default api;